/**
 * Notifications settings (brief §12). A tenant admin adds delivery channels — Telegram, email, SMS,
 * or a phone call — and routes them: alarms at/above a severity floor and/or generator run-status
 * changes. Secrets (tokens) are write-only: they're never returned, and a blank field on edit means
 * "unchanged". Real delivery needs the operator's own provider credentials (Telegram bot, Resend,
 * Twilio); an unconfigured channel is shown as such and simply isn't sent to.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth';
import {
  ApiError,
  type ChannelType,
  type NewNotifyChannel,
  type NotifyChannelRow,
  type NotifyDelivery,
  type Site,
} from '../api';
import { Banner, Button } from '../ui';

interface TelegramLink {
  id: string;
  chat_id: string;
  telegram_username: string | null;
  linked_at: string;
  email: string;
}

const TYPE_LABEL: Record<ChannelType, string> = {
  telegram: 'Telegram',
  email: 'Email',
  sms: 'SMS',
  phone: 'Phone call',
};
const TYPE_HINT: Record<ChannelType, string> = {
  telegram: 'A Telegram bot — needs a bot token and the chat ID to post to.',
  email: 'Email via an HTTP mail API (Resend-compatible).',
  sms: 'SMS via Twilio.',
  phone: 'An automated phone call via Twilio Voice — cuts through for criticals.',
};
interface Field {
  key: string;
  label: string;
  secret?: boolean;
  optional?: boolean;
  placeholder?: string;
}
const FIELDS: Record<ChannelType, Field[]> = {
  telegram: [
    { key: 'bot_token', label: 'Bot token', secret: true, placeholder: '123456:ABC-DEF…' },
    { key: 'chat_id', label: 'Chat ID', placeholder: '-1001234567890' },
  ],
  email: [
    { key: 'api_key', label: 'Mail API key', secret: true },
    { key: 'from', label: 'From address', placeholder: 'alerts@yourco.com' },
    { key: 'to', label: 'To address', placeholder: 'oncall@yourco.com' },
    {
      key: 'api_url',
      label: 'API URL',
      optional: true,
      placeholder: 'https://api.resend.com/emails',
    },
  ],
  sms: [
    { key: 'account_sid', label: 'Twilio Account SID' },
    { key: 'auth_token', label: 'Twilio Auth Token', secret: true },
    { key: 'from', label: 'From number', placeholder: '+61…' },
    { key: 'to', label: 'To number', placeholder: '+61…' },
  ],
  phone: [
    { key: 'account_sid', label: 'Twilio Account SID' },
    { key: 'auth_token', label: 'Twilio Auth Token', secret: true },
    { key: 'from', label: 'From number', placeholder: '+61…' },
    { key: 'to', label: 'To number (called)', placeholder: '+61…' },
  ],
};
const SEVERITIES = ['warning', 'major', 'critical'] as const;

/**
 * Report cadences an operator actually chooses. A linear slider across 5 minutes to 4 hours spends
 * most of its travel on values nobody wants (is 137 minutes meaningfully different from 140?), so
 * the control steps through round intervals instead and the stored value is always one of these.
 */
const CADENCES_MIN = [5, 10, 15, 30, 45, 60, 90, 120, 180, 240];

const cadenceLabel = (mins: number): string =>
  mins < 60
    ? `${mins} minutes`
    : mins === 60
      ? '1 hour'
      : mins % 60 === 0
        ? `${mins / 60} hours`
        : `${Math.floor(mins / 60)}h ${mins % 60}m`;

/** Nearest configured step to a stored value, so an out-of-band value still lands on the scale. */
function nearestIndex(ms: number): number {
  const mins = Math.round(ms / 60000);
  let best = 0;
  for (let i = 1; i < CADENCES_MIN.length; i++) {
    if (Math.abs(CADENCES_MIN[i]! - mins) < Math.abs(CADENCES_MIN[best]! - mins)) best = i;
  }
  return best;
}

export function Notifications(): JSX.Element {
  const { api, session } = useAuth();
  const isAdmin = session?.role === 'tenant_admin';
  const [channels, setChannels] = useState<NotifyChannelRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // add-channel form
  const [type, setType] = useState<ChannelType>('telegram');
  const [name, setName] = useState('');
  const [cfg, setCfg] = useState<Record<string, string>>({});
  const [sev, setSev] = useState('warning');
  const [onAlarms, setOnAlarms] = useState(true);
  const [onRun, setOnRun] = useState(false);
  const [onReport, setOnReport] = useState(true);
  const [scope, setScope] = useState<string>(''); // '' = every site
  const [busy, setBusy] = useState(false);

  // Sites, for scoping a channel. An empty list is not an error — a workspace with no sites yet
  // simply has nothing to narrow to, and the picker says so rather than appearing broken.
  const [sites, setSites] = useState<Site[]>([]);

  // Delivery history
  const [deliveries, setDeliveries] = useState<NotifyDelivery[] | null>(null);
  const [failuresOnly, setFailuresOnly] = useState(false);

  // run-report cadence
  const [cadenceIdx, setCadenceIdx] = useState<number | null>(null);
  const [savingCadence, setSavingCadence] = useState(false);

  async function load() {
    try {
      setChannels((await api.listChannels()).channels);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not load channels.');
    }
  }
  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin]); // load reads only `api`, stable for the session

  useEffect(() => {
    if (!isAdmin) return;
    api
      .listSites()
      .then((r) => setSites(r.sites))
      .catch(() => setSites([]));
  }, [isAdmin, api]);

  const loadDeliveries = useCallback(() => {
    api
      .listDeliveries({ limit: 50, ...(failuresOnly ? { status: 'failed' as const } : {}) })
      .then((r) => setDeliveries(r.deliveries))
      .catch(() => setDeliveries([]));
  }, [api, failuresOnly]);

  useEffect(() => {
    if (isAdmin) loadDeliveries();
  }, [isAdmin, loadDeliveries]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .getNotifySettings()
      .then((s2) => setCadenceIdx(nearestIndex(s2.run_report_ms)))
      .catch(() => setCadenceIdx(nearestIndex(3_600_000)));
  }, [isAdmin, api]);

  // Telegram control pairing
  const [linkCode, setLinkCode] = useState<{ code: string; ttl: number } | null>(null);
  const [links, setLinks] = useState<TelegramLink[] | null>(null);
  const [linking, setLinking] = useState(false);

  const loadLinks = useCallback(() => {
    api
      .telegramLinks()
      .then((r) => setLinks(r.links))
      .catch(() => setLinks([]));
  }, [api]);

  useEffect(() => {
    if (isAdmin) loadLinks();
  }, [isAdmin, loadLinks]);

  async function makeLinkCode() {
    setLinking(true);
    setErr(null);
    try {
      const r = await api.telegramLinkCode();
      setLinkCode({ code: r.code, ttl: r.ttl_seconds });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not create a pairing code.');
    } finally {
      setLinking(false);
    }
  }

  async function revokeLink(id: string) {
    setErr(null);
    try {
      await api.revokeTelegramLink(id);
      loadLinks();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not revoke that link.');
    }
  }

  // The last value the server confirmed. The slider must never sit on a number that was not stored —
  // showing a cadence that did not save is the same lie the API used to tell.
  const savedIdx = useRef<number | null>(null);

  useEffect(() => {
    if (cadenceIdx === null) return;
    if (savedIdx.current === null) {
      savedIdx.current = cadenceIdx; // first value came from the server; nothing to write back
      return;
    }
    if (savedIdx.current === cadenceIdx) return; // a click that landed on the current stop
    // Dragging fires a change per pixel; wait for the handle to settle so one gesture is one write.
    const settle = setTimeout(() => void saveCadence(cadenceIdx), 400);
    return () => clearTimeout(settle);
  }, [cadenceIdx]); // saveCadence reads only `api`, stable for the session

  async function saveCadence(idx: number) {
    setSavingCadence(true);
    setErr(null);
    try {
      await api.setRunReportMs(CADENCES_MIN[idx]! * 60_000);
      savedIdx.current = idx;
      setNote(`Running generators will report every ${cadenceLabel(CADENCES_MIN[idx]!)}.`);
    } catch (e) {
      setCadenceIdx(savedIdx.current); // snap back to what is actually stored
      setErr(e instanceof ApiError ? e.message : 'Could not save the report interval.');
    } finally {
      setSavingCadence(false);
    }
  }

  if (!isAdmin)
    return <Banner kind="err">Admin access is required to manage notifications.</Banner>;

  async function add() {
    setBusy(true);
    setErr(null);
    setNote(null);
    try {
      const body: NewNotifyChannel = {
        type,
        name: name.trim() || TYPE_LABEL[type],
        config: cfg,
        severity_min: sev,
        notify_alarms: onAlarms,
        notify_run_status: onRun,
        notify_daily_report: onReport,
        site_id: scope || null,
      };
      await api.createChannel(body);
      setName('');
      setCfg({});
      await load();
      setNote('Channel added.');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not add the channel.');
    } finally {
      setBusy(false);
    }
  }

  async function patchRouting(ch: NotifyChannelRow, patch: Partial<NotifyChannelRow['routing']>) {
    setChannels((cs) =>
      cs!.map((c) => (c.id === ch.id ? { ...c, routing: { ...c.routing, ...patch } } : c)),
    );
    try {
      await api.updateChannel(ch.id, patch);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not update routing.');
      void load();
    }
  }
  async function toggleEnabled(ch: NotifyChannelRow) {
    try {
      await api.updateChannel(ch.id, { enabled: !ch.enabled });
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not update the channel.');
    }
  }
  async function test(ch: NotifyChannelRow) {
    setErr(null);
    setNote(null);
    try {
      await api.testChannel(ch.id);
      setNote(`Test sent to ${ch.name ?? TYPE_LABEL[ch.type]}.`);
    } catch (e) {
      setErr(e instanceof ApiError ? `Test failed: ${e.message}` : 'Test failed.');
    }
  }
  async function remove(ch: NotifyChannelRow) {
    try {
      await api.deleteChannel(ch.id);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not remove the channel.');
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Notifications</h1>
          <div className="lead">
            Where alarms and run-status changes are delivered. Criticals can ring a phone; warnings
            can go to Telegram or email.
          </div>
        </div>
      </div>

      {err && <Banner kind="err">{err}</Banner>}
      {note && <Banner kind="ok">{note}</Banner>}

      {/* how often a running generator reports in */}
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <div className="section-label">Running-generator reports</div>
        <p className="faint" style={{ marginTop: 0 }}>
          While a generator is running it reports fuel, voltages, frequency and load per phase.
          Start and stop are always sent — this sets how often it reports in between.
        </p>
        {cadenceIdx === null ? (
          <p className="faint" style={{ margin: 0 }}>
            Loading…
          </p>
        ) : (
          <div className="cadence">
            <input
              type="range"
              min={0}
              max={CADENCES_MIN.length - 1}
              step={1}
              value={cadenceIdx}
              onChange={(e) => setCadenceIdx(Number(e.target.value))}
              aria-label="Report interval"
            />
            <div className="cadence-scale">
              <span>5 min</span>
              <strong>
                every {cadenceLabel(CADENCES_MIN[cadenceIdx]!)}
                {savingCadence ? ' — saving…' : ''}
              </strong>
              <span>4 hours</span>
            </div>
          </div>
        )}
      </div>

      {/* telegram control pairing */}
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <div className="section-label">Telegram control</div>
        <p className="faint" style={{ marginTop: 0 }}>
          Start and stop the generator from Telegram. Linking binds a chat to your own account, so
          commands are recorded against you and carry your permissions — the same checks as this
          site, including typing the site code to confirm.
        </p>
        {linkCode ? (
          <div className="linkcode">
            <div className="linkcode-value">{linkCode.code}</div>
            <div className="faint">
              Send <strong>/link {linkCode.code}</strong> to the bot. Expires in{' '}
              {Math.round(linkCode.ttl / 60)} minutes and works once.
            </div>
          </div>
        ) : (
          <Button className="btn" busy={linking} onClick={() => void makeLinkCode()}>
            Create a pairing code
          </Button>
        )}
        {links && links.length > 0 && (
          <table className="tbl" style={{ marginTop: 14 }}>
            <thead>
              <tr>
                <th>Chat</th>
                <th>Account</th>
                <th>Linked</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {links.map((l) => (
                <tr key={l.id}>
                  <td>{l.telegram_username ? `@${l.telegram_username}` : l.chat_id}</td>
                  <td>{l.email}</td>
                  <td className="faint">{new Date(l.linked_at).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Button className="btn btn-sm" onClick={() => void revokeLink(l.id)}>
                      Revoke
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* existing channels */}
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <div className="section-label">Channels</div>
        {!channels ? (
          <p className="faint" style={{ margin: 0 }}>
            Loading…
          </p>
        ) : channels.length === 0 ? (
          <p className="faint" style={{ margin: 0 }}>
            No channels yet — add one below. Until then, alarms show in-app only.
          </p>
        ) : (
          <div className="stack">
            {channels.map((ch) => (
              <div className="row-card" key={ch.id}>
                <div style={{ flex: 1 }}>
                  <div className="title">
                    {ch.name ?? TYPE_LABEL[ch.type]}{' '}
                    <span className="tag tag-param">{TYPE_LABEL[ch.type]}</span>{' '}
                    {!ch.configured && (
                      <span className="pill crit">
                        <span className="dot" />
                        needs config
                      </span>
                    )}
                    {!ch.enabled && (
                      <span className="pill idle" style={{ marginLeft: 6 }}>
                        disabled
                      </span>
                    )}
                  </div>
                  <div className="notify-routing">
                    <label className="chk">
                      <input
                        type="checkbox"
                        checked={ch.routing.notify_alarms}
                        onChange={(e) => void patchRouting(ch, { notify_alarms: e.target.checked })}
                      />
                      Alarms
                    </label>
                    <select
                      className="select select-sm"
                      value={ch.routing.severity_min}
                      disabled={!ch.routing.notify_alarms}
                      onChange={(e) => void patchRouting(ch, { severity_min: e.target.value })}
                    >
                      {SEVERITIES.map((s) => (
                        <option key={s} value={s}>
                          {s} &amp; above
                        </option>
                      ))}
                    </select>
                    <label className="chk">
                      <input
                        type="checkbox"
                        checked={ch.routing.notify_run_status}
                        onChange={(e) =>
                          void patchRouting(ch, { notify_run_status: e.target.checked })
                        }
                      />
                      Run-status changes
                    </label>
                    {(ch.type === 'telegram' || ch.type === 'email') && (
                      <label className="chk">
                        <input
                          type="checkbox"
                          checked={ch.routing.notify_daily_report}
                          onChange={(e) =>
                            void patchRouting(ch, { notify_daily_report: e.target.checked })
                          }
                        />
                        Daily 7am report
                      </label>
                    )}
                    <select
                      className="select select-sm"
                      value={ch.routing.site_id ?? ''}
                      onChange={(e) => void patchRouting(ch, { site_id: e.target.value || null })}
                      aria-label="Sites this channel covers"
                    >
                      <option value="">Every site</option>
                      {sites.map((st) => (
                        <option key={st.id} value={st.id}>
                          {st.name} only
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="row-right">
                  <button className="btn btn-ghost btn-sm" onClick={() => void test(ch)}>
                    Test
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => void toggleEnabled(ch)}>
                    {ch.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => void remove(ch)}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* what was actually delivered */}
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <div className="section-label">Delivery history</div>
        <p className="faint" style={{ marginTop: 0 }}>
          Every send attempt and its outcome. A channel with a revoked token fails silently at the
          provider — this is where that shows up, rather than in a server log nobody reads.
        </p>
        <div className="notify-routing" style={{ marginBottom: 10 }}>
          <label className="chk">
            <input
              type="checkbox"
              checked={failuresOnly}
              onChange={(e) => setFailuresOnly(e.target.checked)}
            />
            Failures only
          </label>
          <button className="btn btn-ghost btn-sm" onClick={() => loadDeliveries()}>
            Refresh
          </button>
        </div>
        {!deliveries ? (
          <p className="faint" style={{ margin: 0 }}>
            Loading…
          </p>
        ) : deliveries.length === 0 ? (
          <p className="faint" style={{ margin: 0 }}>
            {failuresOnly
              ? 'No failed deliveries — every notification has been accepted by its provider.'
              : 'Nothing sent yet.'}
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Channel</th>
                  <th>Message</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.id}>
                    <td className="faint" style={{ whiteSpace: 'nowrap' }}>
                      {new Date(d.created_at).toLocaleString()}
                    </td>
                    <td>
                      {d.channel_name ?? TYPE_LABEL[d.channel_type]}{' '}
                      <span className="tag tag-param">{TYPE_LABEL[d.channel_type]}</span>
                    </td>
                    <td>{d.subject}</td>
                    <td>
                      {d.status === 'sent' ? (
                        <span className="pill good">
                          <span className="dot" />
                          sent
                        </span>
                      ) : (
                        <>
                          <span className="pill crit">
                            <span className="dot" />
                            failed
                          </span>
                          {d.error && (
                            <div className="faint" style={{ marginTop: 4 }}>
                              {d.error}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* add a channel */}
      <div className="panel panel-pad">
        <div className="section-label">Add a channel</div>
        <div className="field">
          <label>Type</label>
          <div className="seg seg-4">
            {(Object.keys(TYPE_LABEL) as ChannelType[]).map((t) => (
              <button
                key={t}
                type="button"
                className={type === t ? 'seg-on' : ''}
                onClick={() => {
                  setType(t);
                  setCfg({});
                }}
              >
                {TYPE_LABEL[t]}
              </button>
            ))}
          </div>
          <div className="hint">{TYPE_HINT[type]}</div>
        </div>
        <div className="field">
          <label htmlFor="nm">Name</label>
          <input
            id="nm"
            className="input"
            placeholder={`${TYPE_LABEL[type]} — on-call`}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        {FIELDS[type].map((f) => (
          <div className="field" key={f.key}>
            <label htmlFor={f.key}>
              {f.label} {f.optional && <span className="opt">(optional)</span>}
            </label>
            <input
              id={f.key}
              className="input mono"
              type={f.secret ? 'password' : 'text'}
              placeholder={f.placeholder}
              autoComplete="off"
              value={cfg[f.key] ?? ''}
              onChange={(e) => setCfg((s) => ({ ...s, [f.key]: e.target.value }))}
            />
          </div>
        ))}
        <div className="field">
          <label>Route</label>
          <div className="notify-routing">
            <label className="chk">
              <input
                type="checkbox"
                checked={onAlarms}
                onChange={(e) => setOnAlarms(e.target.checked)}
              />
              Alarms
            </label>
            <select
              className="select select-sm"
              value={sev}
              disabled={!onAlarms}
              onChange={(e) => setSev(e.target.value)}
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s} &amp; above
                </option>
              ))}
            </select>
            <label className="chk">
              <input type="checkbox" checked={onRun} onChange={(e) => setOnRun(e.target.checked)} />
              Run-status changes
            </label>
            {(type === 'telegram' || type === 'email') && (
              <label className="chk">
                <input
                  type="checkbox"
                  checked={onReport}
                  onChange={(e) => setOnReport(e.target.checked)}
                />
                Daily 7am report
              </label>
            )}
            <select
              className="select select-sm"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              aria-label="Sites this channel covers"
            >
              <option value="">Every site</option>
              {sites.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.name} only
                </option>
              ))}
            </select>
          </div>
          <div className="hint">
            {scope
              ? 'This channel is only used for that site.'
              : 'This channel is used for every site in the workspace.'}
          </div>
        </div>
        <div className="form-actions">
          <span />
          <Button busy={busy} onClick={() => void add()}>
            Add channel
          </Button>
        </div>
      </div>
    </>
  );
}
